import React, { useMemo } from 'react';

import {
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TablePagination,
	Box,
	Checkbox,
} from '@mui/material';

import ContainerLoadingIndicator from 'src/components/common/loading/ContainerLoadingIndicator';
import NoDataView from 'src/components/common/modal/NoDataView';
import { IDataTableProps } from 'src/components/common/types/tableTypes';

import { SearchInput } from './SearchInput';
import { TableDateRangeFilter } from './TableDateRangeFilter';
import { TableFilter } from './TableFilter';
import { TableSort } from './TableSort';

const DATA_TABLE_ROWS_PER_PAGE =
	Number(process.env.REACT_APP_DATA_TABLE_ROWS_PER_PAGE) || 10;

export const DataTable = <T extends { id: string | number }>({
	columns,
	data,
	loadingKey,
	emptyMessage = 'No data available',
	paginationInfo,
	enablePagination = true,
	onQueryChange,
	queryState,
	isTableSelectable = false,
	selectedIds = [],
	onSelectionChange,
	isRowSelectable,
}: IDataTableProps<T>) => {
	const defaultPaginationInfo = { rowsPerPage: DATA_TABLE_ROWS_PER_PAGE };
	const computedPaginationInfo = paginationInfo
		? { ...defaultPaginationInfo, ...paginationInfo }
		: undefined;

	const hasSearch = columns.some((col) => col.searchable);
	const filterableColumns = columns.filter((col) => col.filterable);
	const dateFilterColumn = columns.find((col) => col.dateFilter);

	const handleChangePage = (event: unknown, newPage: number) => {
		onQueryChange?.({ ...queryState, page: newPage });
	};

	const validSelectableRows = useMemo(() => {
		if (!isRowSelectable) return data;
		return data.filter(isRowSelectable);
	}, [data, isRowSelectable]);

	const isAllSelected =
		validSelectableRows.length > 0 &&
		validSelectableRows.every((row) => selectedIds.includes(row.id));

	const isIndeterminate =
		validSelectableRows.some((row) => selectedIds.includes(row.id)) &&
		!isAllSelected;

	const handleSelectAllClick = (
		event: React.ChangeEvent<HTMLInputElement>,
	) => {
		if (!onSelectionChange) return;

		const { checked } = event.target;
		const currentVisibleIds = validSelectableRows.map((row) => row.id);

		if (checked) {
			if (isIndeterminate) {
				const visibleSelectedIds = validSelectableRows
					.filter((row) => selectedIds.includes(row.id))
					.map((row) => row.id);

				onSelectionChange(
					selectedIds.filter(
						(id) => !visibleSelectedIds.includes(id),
					),
				);
			} else {
				const mergedIds = Array.from(
					new Set([...selectedIds, ...currentVisibleIds]),
				);
				onSelectionChange(mergedIds);
			}
		} else {
			const filteredIds = selectedIds.filter(
				(id) => !currentVisibleIds.includes(id),
			);
			onSelectionChange(filteredIds);
		}
	};

	const handleRowClick = (
		event: React.ChangeEvent<HTMLInputElement>,
		id: string | number,
	) => {
		if (!onSelectionChange) return;

		const { checked } = event.target;

		if (checked) {
			onSelectionChange([...selectedIds, id]);
		} else {
			onSelectionChange(selectedIds.filter((itemId) => itemId !== id));
		}
	};

	return (
		<Box sx={{ width: '100%', position: 'relative' }}>
			<ContainerLoadingIndicator loadingKey={loadingKey} />

			<Box
				sx={{
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'center',
					marginBottom: 2,
				}}
			>
				{/* Search Field */}
				{hasSearch && queryState && onQueryChange && (
					<SearchInput
						initialValue={queryState.searchText || ''}
						onSearch={(text) =>
							onQueryChange?.({
								...queryState,
								searchText: text,
								page: 0,
							})
						}
						onClear={() =>
							onQueryChange?.({
								...queryState,
								searchText: '',
								page: 0,
							})
						}
					/>
				)}

				<Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
					{/* Filter by Date*/}
					{dateFilterColumn && queryState && onQueryChange && (
						<TableDateRangeFilter
							queryState={queryState}
							onQueryChange={onQueryChange}
						/>
					)}
					{/* Filter by Value */}
					{queryState &&
						onQueryChange &&
						filterableColumns.map((col) => (
							<TableFilter
								key={col.key.toString()}
								column={col}
								queryState={queryState}
								onQueryChange={onQueryChange}
							/>
						))}
				</Box>
			</Box>

			<TableContainer sx={{ overflowX: 'auto', width: '100%' }}>
				<Table
					sx={{
						tableLayout: {
							xs: 'auto',
							sm: 'fixed',
						},
					}}
				>
					<TableHead>
						<TableRow>
							{isTableSelectable && (
								<TableCell
									padding='checkbox'
									sx={{ width: '50px' }}
								>
									<Checkbox
										color='primary'
										indeterminate={isIndeterminate}
										checked={isAllSelected}
										onChange={handleSelectAllClick}
										disabled={
											validSelectableRows.length === 0
										}
										size='small'
										inputProps={{
											'aria-label': 'select all rows',
										}}
									/>
								</TableCell>
							)}

							{columns.map((column) => (
								<TableCell
									key={column.key.toString()}
									align={column.align || 'left'}
									sx={{
										width: column.width || 'auto',
									}}
								>
									<Box
										sx={{
											display: 'flex',
											alignItems: 'center',
										}}
									>
										{column.label}
										{column.sortable &&
											onQueryChange &&
											queryState && (
												<TableSort
													column={column}
													queryState={queryState}
													onQueryChange={
														onQueryChange
													}
												/>
											)}
									</Box>
								</TableCell>
							))}
						</TableRow>
					</TableHead>
					<TableBody>
						{data.length === 0 ? (
							<TableRow>
								<TableCell
									colSpan={
										isTableSelectable
											? columns.length + 1
											: columns.length
									}
									align='center'
									sx={{ py: 3 }}
								>
									<NoDataView message={emptyMessage} />
								</TableCell>
							</TableRow>
						) : (
							data.map((item, index) => {
								const isItemSelected = selectedIds.includes(
									item.id,
								);
								const isItemSelectable = isRowSelectable
									? isRowSelectable(item)
									: false;

								return (
									<TableRow
										key={item.id}
										selected={isItemSelected}
										hover={
											isItemSelectable &&
											isTableSelectable
										}
									>
										{isTableSelectable && (
											<TableCell padding='checkbox'>
												<Checkbox
													color='primary'
													checked={isItemSelected}
													onChange={(e) =>
														handleRowClick(
															e,
															item.id,
														)
													}
													size='small'
													disabled={!isItemSelectable}
													inputProps={{
														'aria-labelledby': `table-checkbox-${index}`,
													}}
												/>
											</TableCell>
										)}

										{columns.map((column) => (
											<TableCell
												key={`${item.id}-${column.key.toString()}`}
												align={column.align || 'left'}
											>
												{column.render
													? column.render(item, index)
													: (item[
															column.key as keyof T
														] as React.ReactNode)}
											</TableCell>
										))}
									</TableRow>
								);
							})
						)}
					</TableBody>
				</Table>
			</TableContainer>

			{enablePagination &&
				computedPaginationInfo &&
				queryState &&
				onQueryChange && (
					<TablePagination
						component='div'
						count={computedPaginationInfo.totalCount}
						page={queryState.page || 0}
						onPageChange={handleChangePage}
						rowsPerPage={computedPaginationInfo.rowsPerPage}
						rowsPerPageOptions={[
							computedPaginationInfo.rowsPerPage,
						]}
					/>
				)}
		</Box>
	);
};
